import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaIndustry,
  FaMicroscope,
  FaChartLine,
  FaRobot,
  FaCogs,
  FaDatabase,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

const services = [
  {
    icon: <FaIndustry className="text-3xl text-sky-600" />,
    title: "Manufacturing AI & Smart Operations",
    tag: "Manufacturing",
    headline: "Reduce defects, downtime, and waste with intelligent automation.",
    desc: "We deploy machine-learning models directly into your production environment to automate quality inspection, predict equipment failures before they occur, and optimize throughput in real time. Our solutions integrate seamlessly with existing PLCs, SCADA, and ERP systems.",
    outcomes: [
      "Up to 60% reduction in unplanned downtime",
      "Automated visual quality inspection at line speed",
      "Real-time OEE dashboards and anomaly alerts",
      "Predictive maintenance scheduling integration",
    ],
  },
  {
    icon: <FaMicroscope className="text-3xl text-emerald-600" />,
    title: "Life Sciences MES & Compliance",
    tag: "Life Sciences",
    headline: "Go paperless, stay audit-ready, and accelerate FDA submissions.",
    desc: "XKMINNE's life sciences practice transforms manual, paper-based manufacturing workflows into fully digital, auditable systems. We implement and configure MES platforms with GxP compliance baked in — including electronic batch records, deviation management, and CAPA workflows.",
    outcomes: [
      "FDA 21 CFR Part 11 compliant electronic records",
      "ISO 13485 and GMP-ready audit trails",
      "Automated deviation, CAPA, and change control",
      "Reduced batch review cycle from days to hours",
    ],
  },
  {
    icon: <FaChartLine className="text-3xl text-violet-600" />,
    title: "SAP Supply Chain Transformation",
    tag: "Supply Chain",
    headline: "Unlock end-to-end supply chain visibility with SAP and AI.",
    desc: "Our SAP specialists deliver IBP, PP/DS, TM, and QM implementations that are deeply integrated with LLM-powered analytics layers. We translate complex supply chain data into clear, actionable intelligence — enabling smarter demand planning, faster procurement cycles, and resilient logistics networks.",
    outcomes: [
      "SAP IBP, PP/DS, TM, and S/4HANA deployments",
      "LLM-powered demand forecasting and sensing",
      "Real-time supplier risk and inventory optimization",
      "38% average reduction in supply chain operating costs",
    ],
  },
  {
    icon: <FaRobot className="text-3xl text-rose-500" />,
    title: "Digital Twin Execution",
    tag: "Digital Twin",
    headline: "Mirror your physical operations with live, intelligent digital models.",
    desc: "We build high-fidelity digital twins that synchronize with your physical assets in real time — bridging the OT/IT gap and enabling simulation-driven decision making. From process optimization to scenario planning, our digital twin solutions deliver continuous operational intelligence.",
    outcomes: [
      "Real-time OT/IT data synchronization",
      "Simulation-based capacity and throughput planning",
      "Root cause analysis accelerated by 70%",
      "Continuous process improvement feedback loops",
    ],
  },
  {
    icon: <FaCogs className="text-3xl text-amber-500" />,
    title: "Regulatory Affairs & Validation",
    tag: "Compliance",
    headline: "Navigate FDA, EMA, and ISO requirements with confidence.",
    desc: "Our regulatory consultants support computer system validation (CSV), 21 CFR Part 11 compliance, IQ/OQ/PQ protocols, and technical documentation for software systems in regulated environments. We help you stay compliant while moving fast.",
    outcomes: [
      "IQ/OQ/PQ validation protocol authoring and execution",
      "21 CFR Part 11 and Annex 11 gap assessments",
      "Risk-based approach to CSV per GAMP 5",
      "Regulatory submission technical documentation support",
    ],
  },
  {
    icon: <FaDatabase className="text-3xl text-teal-500" />,
    title: "Data & AI Platform Engineering",
    tag: "Data & AI",
    headline: "Build the data foundation that makes intelligent operations possible.",
    desc: "Enterprise AI is only as good as the data underneath it. We design and deploy scalable data platforms — from cloud data lakes to edge analytics pipelines — that unify your operational data and power every AI and analytics use case across your business.",
    outcomes: [
      "Unified operational data lake architecture",
      "Real-time edge analytics and IoT data pipelines",
      "Custom LLM fine-tuning on your proprietary data",
      "Self-service BI and executive dashboards",
    ],
  },
];

export default function Services() {
  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-24 px-6 xl:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            Our Services
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Specialized Solutions That Deliver Measurable Results
          </motion.h1>
          <motion.p
            className="text-blue-200 text-lg max-w-2xl mx-auto mb-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            From AI-powered manufacturing to FDA-compliant MES and SAP transformations — every engagement is designed to create quantifiable business value.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-blue-800 font-bold px-7 py-3.5 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              Discuss Your Project <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Service Cards ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto space-y-10">
          {services.map((s, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden md:flex"
            >
              <div className="md:w-2/3 p-8 md:p-10">
                <span className="inline-block bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                  {s.tag}
                </span>
                <div className="flex items-center gap-3 mb-3">
                  {s.icon}
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">{s.title}</h2>
                </div>
                <p className="text-base font-semibold text-blue-700 mb-3">{s.headline}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
              <div className="md:w-1/3 bg-gradient-to-br from-sky-50 to-blue-50 p-8 md:p-10 flex flex-col justify-center">
                <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wide mb-4">Key Outcomes</h4>
                <ul className="space-y-3">
                  {s.outcomes.map((o, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                      <FaCheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Industries We Serve</p>
          <h2 className="text-3xl font-bold text-blue-900 mb-10">Built for Regulated, Complex Environments</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              "Medical Devices (Class II & III)",
              "Pharmaceutical & Biotech",
              "Industrial Manufacturing",
              "Diagnostics & In Vitro",
              "Contract Manufacturing (CMO/CDMO)",
              "Food & Beverage (GMP)",
            ].map((industry, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-5 rounded-xl border border-gray-100 bg-gray-50 text-sm font-semibold text-gray-700 text-left flex items-center gap-3"
              >
                <FaCheckCircle className="text-emerald-500 flex-shrink-0" />
                {industry}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-blue-900 text-white text-center px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">Not Sure Which Service Fits?</h2>
          <p className="text-blue-200 mb-8">
            Book a free 30-minute discovery call. Our consultants will review your current environment and recommend the highest-ROI starting point — no commitment required.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
          >
            Book a Free Discovery Call <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 text-center">
        <span className="text-xl font-extrabold text-white">
          XK<span className="text-sky-400">MINNE</span>
        </span>
        <p className="text-sm mt-3 text-gray-500">
          &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
